import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
export type ThemePreference = "light" | "dark" | "system";
interface ThemeContextValue {
  preference: ThemePreference;
  resolved: "light" | "dark";
  setPreference: (p: ThemePreference) => void;
  toggle: () => void;
}
const ThemeContext = createContext<ThemeContextValue | null>(null);
const STORAGE_KEY = "polylexa-theme";
function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}
export function ThemeProvider({
  children
}: {
  children: ReactNode;
}) {
  const [preference, setPreference] = useState<ThemePreference>(() => {
    if (typeof window === "undefined") return "system";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
  });
  const [systemDark, setSystemDark] = useState(systemPrefersDark);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const resolved: "light" | "dark" = preference === "system" ? systemDark ? "dark" : "light" : preference;
  useEffect(() => {
    document.documentElement.classList.toggle("dark", resolved === "dark");
    window.localStorage.setItem(STORAGE_KEY, preference);
  }, [resolved, preference]);
  const value = useMemo<ThemeContextValue>(() => ({
    preference,
    resolved,
    setPreference,
    toggle: () => setPreference(resolved === "dark" ? "light" : "dark")
  }), [preference, resolved]);
  return <ThemeContext.Provider value={value} data-id="element-1249" data-mp="xo885q0vmjm6">{children}</ThemeContext.Provider>;
}
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}